// src/components/layouts/Newsletter.tsx
import { useState } from 'react';
import { TitleMedium } from '@/components/typography';
import { PrimaryButton } from '@/components/buttons';
import { Input } from '@/components/elements';
import { LazyVideo } from '@/components/elements';
import Anim from '@/assets/anim/newsletter.webm';
import CloseIcon from '@/assets/img/icon/22x24/x-close.svg';

interface NewsletterProps {
    // Affects the layout of the left /text column
    modalView?: boolean;
    onClose?: () => void;
}

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

export const Newsletter = ({modalView = false, onClose}: NewsletterProps) => {
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);
    
    const handleSubmit = async () => {
        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email.trim()) {
            setMessage({ type: 'error', text: 'Please enter your email address' });
            return;
        }
        
        if (!emailRegex.test(email)) {
            setMessage({ type: 'error', text: 'Please enter a valid email address' });
            return;
        }

        setIsSubmitting(true);
        setMessage(null);

        try {
            const response = await fetch(`${API_BASE}/api/newsletter/subscribe`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email: email.trim() }),
            });

            const data = await response.json();

            if (response.ok) {
                setMessage({ 
                    type: 'success', 
                    text: 'Successfully subscribed! Check your inbox for updates.' 
                });
                setEmail(''); // Clear input on success
                
                // Auto-close modal after 2 seconds if in modal view
                if (modalView && onClose) {
                    setTimeout(() => {
                        onClose();
                    }, 2000);
                }
            } else {
                // Handle specific error messages
                if (response.status === 429) {
                    setMessage({ 
                        type: 'error', 
                        text: 'Too many requests. Please try again in a minute.' 
                    });
                } else {
                    setMessage({ 
                        type: 'error', 
                        text: data.detail || 'Subscription failed. Please try again.' 
                    });
                }
            }
        } catch (error) {
            console.error('Newsletter subscription error:', error);
            setMessage({ 
                type: 'error', 
                text: 'Network error. Please check your connection and try again.' 
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Allow Enter key to submit
    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !isSubmitting) {
            handleSubmit();
        }
    };

    return (
        <section className="w-full flex justify-center px-4 py-8 lg:py-16">
            <div className="w-full max-w-[320px] lg:max-w-[1200px] relative">
                {/* Newsletter Card */}
                <div className="relative w-full lg:min-w-[820px] bg-gradient-to-br from-white/20 to-white/5 rounded-3xl shadow-[0px_0.9585345387458801px_23.963363647460938px_0px_rgba(69,42,124,0.10)] border-2 border-white/10 backdrop-blur-xl">
                    {/* Background Glow Effects */}
                    <div className='absolute w-full h-full -z-10 overflow-hidden rounded-3xl top-0 left-0'>
                        <div className="absolute w-56 h-56 lg:w-72 lg:h-72 left-[227px] top-[75px] lg:left-[281px] lg:top-[162px] bg-secondary/60 rounded-full blur-[157px] opacity-60" />
                        <div className="absolute w-60 h-72 lg:w-72 lg:h-80 right-[-44px] top-[-65px] lg:right-[-44px] lg:top-[-151px] bg-primary/40 blur-[104px]" />
                        <div className="absolute w-36 h-40 lg:w-44 lg:h-48 right-[47px] top-[-112px] lg:right-[105px] lg:top-[-210px] bg-secondary rounded-full blur-[104px]" />
                    </div>

                    {/* Close Button */}
                    {modalView && onClose && (
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 z-50 hover:opacity-70 transition-opacity"
                            aria-label="Close newsletter"
                        >
                            <img src={CloseIcon} alt="Close" className="w-[16px] h-[16px]" />
                        </button>
                    )}

                    <div className='relative z-0 w-full h-full pointer-events-none'>
                        <LazyVideo
                            src={Anim}
                            className="absolute hidden lg:block scale-x-[-1] left-[80%] top-20 -translate-x-1/2 -translate-y-1/2 max-w-[1180px] w-auto h-[625px]"
                        />
                    </div>

                    <div className="relative z-10 flex flex-col justify-center items-start gap-4 lg:gap-10 p-4 lg:p-[52px] h-full">
                        <div className={`flex flex-col gap-4 lg:gap-8 w-full ${modalView ? 'lg:max-w-[391px]' : ''}`}>
                            <TitleMedium className="text-white max-w-[288px] lg:max-w-[570px]">
                                Get launch, audit and first-auction updates
                            </TitleMedium>
                            <div className={`flex flex-col gap-4 w-full ${!modalView ? 'lg:flex-row lg:gap-0 lg:max-w-[600px] lg:relative' : ''}`}>
                                <Input
                                    type="email"
                                    placeholder="Your Email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    disabled={isSubmitting}
                                    className={`w-full h-[60px] lg:h-[64px] ${!modalView ? 'lg:w-[600px] lg:relative lg:z-10' : ''}`}
                                />
                                <PrimaryButton 
                                    onClick={handleSubmit}
                                    disabled={isSubmitting}
                                    className={`w-full ${!modalView ? 'lg:w-52 lg:absolute lg:top-1 lg:right-1 lg:z-20' : ''}`}
                                >
                                    {isSubmitting ? 'Subscribing...' : 'Subscribe for Launch'}
                                </PrimaryButton>
                            </div>
                            
                            {/* Success/Error Message */}
                            {message && (
                                <div 
                                    className={`text-sm font-medium transition-opacity duration-300 ${
                                        message.type === 'success' 
                                            ? 'text-prinary' 
                                            : 'text-error'
                                    }`}
                                >
                                    {message.text}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};